import { getSupabase, type Session, type User } from "@dwelldrop/supabase";
import type { Dispatch, SetStateAction } from "react";
import {
  logAuditEvent,
  resetRateLimit,
} from "../security";
import type { MfaSupabaseClient } from "./aal";
import {
  mfaCleanupUnverifiedTotpFactors,
  mfaGetAssuranceLevel,
  mfaGetVerifiedTotpFactor,
  mfaHasVerifiedTotpFactor,
  mfaSessionNeedsChallenge,
} from "./aal";
import {
  clearMfaFailedAttempts,
  getMfaLockoutMessage,
  recordMfaFailedAttempt,
} from "./rateLimit";
import type {
  MfaAppConfig,
  MfaPendingSession,
  MfaProfilePhone,
  MfaRateLimitState,
  MfaTotpEnrollResult,
  TwoFactorState,
} from "./types";
import {
  MFA_TOTP_CODE_LENGTH,
  isValidMfaRecoveryCodeFormat,
  normalizeMfaRecoveryCode,
} from "./utils";

function emptyMfaChallengeIds(): Pick<
  TwoFactorState,
  "mfaFactorId" | "mfaChallengeId"
> {
  return { mfaFactorId: null, mfaChallengeId: null };
}

export async function mfaChallengeTotpFactor(
  supabase: MfaSupabaseClient,
  factorId: string
): Promise<{ challengeId: string | null; error: string | null }> {
  const { data, error } = await supabase.auth.mfa.challenge({
    factorId,
  });
  if (error) {
    return { challengeId: null, error: error.message };
  }
  return { challengeId: data.id, error: null };
}

export async function mfaVerifyChallengeCode(
  supabase: MfaSupabaseClient,
  factorId: string,
  challengeId: string,
  code: string
): Promise<{ error: string | null }> {
  const verifyPromise = supabase.auth.mfa.verify({
    factorId,
    challengeId,
    code,
  });
  const timeoutMs = 30_000;
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(
      () =>
        reject(
          new Error(
            "MFA verification timed out. Please try again or sign in again."
          )
        ),
      timeoutMs
    );
  });
  try {
    const { error } = await Promise.race([verifyPromise, timeoutPromise]);
    if (error) {
      return { error: error.message };
    }
    return { error: null };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "MFA verification failed.";
    return { error: message };
  }
}

export async function checkMfaEnabled(
  tokenStorageKey: string
): Promise<boolean> {
  const supabase = getSupabase(tokenStorageKey);
  return mfaHasVerifiedTotpFactor(supabase);
}


export type MfaAfterPasswordSignInParams<TProfile extends MfaProfilePhone> = {
  config: MfaAppConfig;
  userId: string;
  profile: TProfile;
  rateLimit: MfaRateLimitState;
  setRateLimit: Dispatch<SetStateAction<MfaRateLimitState>>;
  setPendingSession: Dispatch<
    SetStateAction<MfaPendingSession<TProfile> | null>
  >;
  setTwoFactor: Dispatch<SetStateAction<TwoFactorState>>;
  rememberMe: boolean;
  user: MfaPendingSession<TProfile>["user"];
  session: Session;
};

export type MfaAfterPasswordSignInResult = {
  needsMfaChallenge: boolean;
  smsError: string | null;
};

export async function mfaAfterPasswordSignIn<TProfile extends MfaProfilePhone>(
  params: MfaAfterPasswordSignInParams<TProfile>
): Promise<MfaAfterPasswordSignInResult> {
  const {
    config,
    profile,
    setPendingSession,
    setTwoFactor,
    rememberMe,
    user,
    session,
  } = params;

  const supabase = getSupabase(config.tokenStorageKey);
  const { data: aal } = await mfaGetAssuranceLevel(supabase);

  if (!mfaSessionNeedsChallenge(aal)) {
    return { needsMfaChallenge: false, smsError: null };
  }

  const factor = await mfaGetVerifiedTotpFactor(supabase);
  if (!factor) {
    return {
      needsMfaChallenge: false,
      smsError: "MFA is misconfigured. Contact support.",
    };
  }

  setPendingSession({
    user,
    session,
    profile,
    rememberMe,
  });

  const challenged = await mfaChallengeTotpFactor(supabase, factor.id);

  setTwoFactor({
    enabled: true,
    pendingVerification: true,
    maskedPhone: null,
    smsError: challenged.error,
    mfaFactorId: factor.id,
    mfaChallengeId: challenged.challengeId,
  });

  return { needsMfaChallenge: true, smsError: challenged.error };
}

export type MfaVerifyLoginChallengeParams<TProfile extends MfaProfilePhone> = {
  config: MfaAppConfig;
  code: string;
  factorId: string;
  challengeId: string;
  pendingSession: MfaPendingSession<TProfile> | null;
  isLockedOut: () => boolean;
  lockedUntil: number | null;
  setRateLimit: Dispatch<SetStateAction<MfaRateLimitState>>;
  setPendingSession: Dispatch<
    SetStateAction<MfaPendingSession<TProfile> | null>
  >;
  setTwoFactor: Dispatch<SetStateAction<TwoFactorState>>;
  onLoginSuccess: (
    pending: MfaPendingSession<TProfile>,
    session: Session
  ) => void | Promise<void>;
};

export async function mfaVerifyLoginChallenge<TProfile extends MfaProfilePhone>(
  params: MfaVerifyLoginChallengeParams<TProfile>
): Promise<{ error: string | null }> {
  const {
    config,
    code,
    factorId,
    challengeId,
    pendingSession,
    isLockedOut,
    lockedUntil,
    setRateLimit,
    setPendingSession,
    setTwoFactor,
    onLoginSuccess,
  } = params;

  if (isLockedOut()) {
    return { error: getMfaLockoutMessage(lockedUntil) };
  }

  if (code.length !== MFA_TOTP_CODE_LENGTH || !/^\d+$/.test(code)) {
    return { error: "Please enter a valid 6-digit code." };
  }

  if (!pendingSession) {
    return { error: "No pending login session. Please sign in again." };
  }

  const supabase = getSupabase(config.tokenStorageKey);
  const { error: verifyError } = await mfaVerifyChallengeCode(
    supabase,
    factorId,
    challengeId,
    code
  );

  if (verifyError) {
    const nowLocked = recordMfaFailedAttempt(setRateLimit);
    await logAuditEvent(
      supabase,
      "2fa_verify_failure",
      pendingSession.user.id,
      { app: config.app, method: "totp" },
      "auth",
      config.auditActionSource()
    );
    if (nowLocked) {
      return { error: getMfaLockoutMessage(lockedUntil) };
    }
    return { error: "Invalid code. Please try again." };
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    return {
      error:
        "Verification succeeded but session could not be restored. Please sign in again.",
    };
  }

  await logAuditEvent(
    supabase,
    "2fa_verify_success",
    session.user.id,
    { app: config.app, method: "totp" },
    "auth",
    config.auditActionSource()
  );
  await logAuditEvent(
    supabase,
    "login_success",
    session.user.id,
    { app: config.app, method: "2fa_totp" },
    "auth",
    config.auditActionSource()
  );

  const email = pendingSession.user.email;
  if (email) {
    await resetRateLimit(supabase, config.loginRateKey(email));
  }

  setTwoFactor({
    enabled: true,
    pendingVerification: false,
    maskedPhone: null,
    smsError: null,
    ...emptyMfaChallengeIds(),
  });
  clearMfaFailedAttempts(setRateLimit);
  await onLoginSuccess(pendingSession, session);
  setPendingSession(null);
  return { error: null };
}

export type MfaResendLoginChallengeParams = {
  config: MfaAppConfig;
  factorId: string;
  userId?: string;
  phoneE164?: string;
  rateLimit?: MfaRateLimitState;
  setRateLimit?: Dispatch<SetStateAction<MfaRateLimitState>>;
  setTwoFactor: Dispatch<SetStateAction<TwoFactorState>>;
};

/** Re-challenge TOTP factor if challenge expired */
export async function mfaResendLoginChallenge(
  params: MfaResendLoginChallengeParams
): Promise<{ error: string | null; challengeId: string | null }> {
  const { config, factorId, setTwoFactor } = params;
  const supabase = getSupabase(config.tokenStorageKey);
  const challenged = await mfaChallengeTotpFactor(supabase, factorId);
  if (challenged.error) {
    return { error: challenged.error, challengeId: null };
  }
  setTwoFactor((s) => ({
    ...s,
    smsError: null,
    mfaChallengeId: challenged.challengeId,
  }));
  return { error: null, challengeId: challenged.challengeId };
}


export type MfaEnrollTotpParams = {
  config: MfaAppConfig;
  issuer?: string;
};

export async function mfaEnrollTotp(
  params: MfaEnrollTotpParams
): Promise<MfaTotpEnrollResult> {
  const { config, issuer } = params;
  const supabase = getSupabase(config.tokenStorageKey);

  // Clean up any unverified TOTP factors first
  await mfaCleanupUnverifiedTotpFactors(supabase);

  const appIssuer =
    issuer ??
    config.issuer ??
    (config.app === "admin" ? "DwellDrop Admin" : "DwellDrop Venue");

  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: "totp",
    issuer: appIssuer,
  });

  if (error || !data) {
    return {
      error: error?.message ?? "Failed to enroll TOTP factor.",
      factorId: "",
      qrCode: "",
      secret: "",
      uri: "",
    };
  }

  return {
    error: null,
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
    uri: data.totp.uri,
  };
}

export type MfaEnrollSendChallengeParams = {
  config: MfaAppConfig;
  phone?: string;
  userId?: string | null;
  rateLimit?: MfaRateLimitState;
  setRateLimit?: Dispatch<SetStateAction<MfaRateLimitState>>;
};

/** @deprecated Transition compatibility wrapper. Use mfaEnrollTotp instead. */
export async function mfaEnrollSendChallenge(
  params: MfaEnrollSendChallengeParams

): Promise<{
  error: string | null;
  factorId: string | null;
  challengeId: string | null;
}> {
  const result = await mfaEnrollTotp({ config: params.config });
  if (result.error) {
    return { error: result.error, factorId: null, challengeId: null };
  }
  return { error: null, factorId: result.factorId, challengeId: null };
}

export type MfaEnrollVerifyParams = {
  config: MfaAppConfig;
  code: string;
  factorId: string;
  challengeId?: string | null;
  phone?: string;
  userId: string | null;
  setTwoFactor: Dispatch<SetStateAction<TwoFactorState>>;
};

export async function mfaEnrollVerify(
  params: MfaEnrollVerifyParams
): Promise<{ error: string | null }> {
  const { config, code, factorId, userId, setTwoFactor } = params;

  if (code.length !== MFA_TOTP_CODE_LENGTH || !/^\d+$/.test(code)) {
    return { error: "Please enter a valid 6-digit code." };
  }

  const supabase = getSupabase(config.tokenStorageKey);
  let challengeId = params.challengeId;
  if (!challengeId) {
    const challenged = await mfaChallengeTotpFactor(supabase, factorId);
    if (challenged.error || !challenged.challengeId) {
      return {
        error: challenged.error ?? "Failed to create verification challenge.",
      };
    }
    challengeId = challenged.challengeId;
  }

  const { error: verifyError } = await mfaVerifyChallengeCode(
    supabase,
    factorId,
    challengeId,
    code
  );

  if (verifyError) {
    return { error: "Invalid code. Please try again." };
  }

  setTwoFactor({
    enabled: true,
    pendingVerification: false,
    maskedPhone: null,
    smsError: null,
    ...emptyMfaChallengeIds(),
  });

  void logAuditEvent(
    supabase,
    "2fa_enabled",
    userId,
    { app: config.app, method: "totp" },
    "auth",
    config.auditActionSource()
  );

  return { error: null };
}

export type MfaDisableSendChallengeParams = {
  config: MfaAppConfig;
  userId: string | null;
  rateLimit: MfaRateLimitState;
  setRateLimit: Dispatch<SetStateAction<MfaRateLimitState>>;
  setTwoFactor: Dispatch<SetStateAction<TwoFactorState>>;
  isLockedOut: () => boolean;
  lockedUntil: number | null;
};

export async function mfaDisableSendChallenge(
  params: MfaDisableSendChallengeParams
): Promise<{
  error: string | null;
  factorId: string | null;
  challengeId: string | null;
}> {
  const { config, setTwoFactor, isLockedOut, lockedUntil } = params;

  if (isLockedOut()) {
    return {
      error: getMfaLockoutMessage(lockedUntil),
      factorId: null,
      challengeId: null,
    };
  }

  const supabase = getSupabase(config.tokenStorageKey);
  const factor = await mfaGetVerifiedTotpFactor(supabase);
  if (!factor) {
    setTwoFactor({
      enabled: false,
      pendingVerification: false,
      maskedPhone: null,
      smsError: null,
      ...emptyMfaChallengeIds(),
    });
    return { error: null, factorId: null, challengeId: null };
  }

  const challenged = await mfaChallengeTotpFactor(supabase, factor.id);
  if (challenged.error || !challenged.challengeId) {
    return {
      error: challenged.error ?? "Failed to create challenge.",
      factorId: null,
      challengeId: null,
    };
  }

  setTwoFactor((s) => ({
    ...s,
    mfaFactorId: factor.id,
    mfaChallengeId: challenged.challengeId,
    smsError: null,
    maskedPhone: null,
  }));

  return {
    error: null,
    factorId: factor.id,
    challengeId: challenged.challengeId,
  };
}

export type MfaDisableVerifyAndUnenrollParams = {
  config: MfaAppConfig;
  code: string;
  factorId: string;
  challengeId: string;
  userId: string | null;
  setTwoFactor: Dispatch<SetStateAction<TwoFactorState>>;
  isLockedOut: () => boolean;
  lockedUntil: number | null;
  setRateLimit: Dispatch<SetStateAction<MfaRateLimitState>>;
};

export async function mfaDisableVerifyAndUnenroll(
  params: MfaDisableVerifyAndUnenrollParams
): Promise<{ error: string | null }> {
  const {
    config,
    code,
    factorId,
    challengeId,
    userId,
    setTwoFactor,
    isLockedOut,
    lockedUntil,
    setRateLimit,
  } = params;

  if (isLockedOut()) {
    return { error: getMfaLockoutMessage(lockedUntil) };
  }
  if (code.length !== MFA_TOTP_CODE_LENGTH || !/^\d+$/.test(code)) {
    return { error: "Please enter a valid 6-digit code." };
  }

  const supabase = getSupabase(config.tokenStorageKey);
  const { error: verifyError } = await mfaVerifyChallengeCode(
    supabase,
    factorId,
    challengeId,
    code
  );
  if (verifyError) {
    const nowLocked = recordMfaFailedAttempt(setRateLimit);
    await logAuditEvent(
      supabase,
      "2fa_verify_failure",
      userId,
      { app: config.app, purpose: "disable_2fa" },
      "auth",
      config.auditActionSource()
    );
    if (nowLocked) {
      return { error: getMfaLockoutMessage(lockedUntil) };
    }
    return { error: "Invalid code. Please try again." };
  }

  clearMfaFailedAttempts(setRateLimit);

  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) return { error: error.message };

  await logAuditEvent(
    supabase,
    "2fa_disabled",
    userId,
    { app: config.app },
    "auth",
    config.auditActionSource()
  );

  setTwoFactor({
    enabled: false,
    pendingVerification: false,
    maskedPhone: null,
    smsError: null,
    ...emptyMfaChallengeIds(),
  });

  return { error: null };
}

export async function mfaDisable(
  config: MfaAppConfig,
  userId: string | null,
  setTwoFactor: Dispatch<SetStateAction<TwoFactorState>>
): Promise<{ error: string | null }> {
  const supabase = getSupabase(config.tokenStorageKey);
  const factor = await mfaGetVerifiedTotpFactor(supabase);
  if (!factor) {
    setTwoFactor({
      enabled: false,
      pendingVerification: false,
      maskedPhone: null,
      smsError: null,
      ...emptyMfaChallengeIds(),
    });
    return { error: null };
  }
  return {
    error:
      "Verification required. Confirm with the code from your authenticator app before disabling 2FA.",
  };
}

export type MfaTwoFactorSessionBits = Pick<
  TwoFactorState,
  | "enabled"
  | "maskedPhone"
  | "pendingVerification"
  | "mfaFactorId"
  | "mfaChallengeId"
  | "smsError"
>;

export async function buildMfaTwoFactorFromSession(
  tokenStorageKey: string,
  _profilePhone?: string | null
): Promise<MfaTwoFactorSessionBits> {
  const supabase = getSupabase(tokenStorageKey);
  const enabled = await mfaHasVerifiedTotpFactor(supabase);
  let mfaFactorId: string | null = null;
  if (enabled) {
    const factor = await mfaGetVerifiedTotpFactor(supabase);
    mfaFactorId = factor?.id ?? null;
  }

  const { data: aal } = await mfaGetAssuranceLevel(supabase);
  const pendingVerification = enabled && mfaSessionNeedsChallenge(aal);

  return {
    enabled,
    maskedPhone: null,
    pendingVerification,
    mfaFactorId: pendingVerification ? mfaFactorId : null,
    mfaChallengeId: null,
    smsError: null,
  };
}

export type MfaHydrateAfterSessionRestoreResult<
  TProfile extends MfaProfilePhone,
> = {
  twoFactor: MfaTwoFactorSessionBits;
  pendingSession: MfaPendingSession<TProfile> | null;
  needsMfaChallenge: boolean;
};

/** After restoring a persisted session, enforce AAL2 when the user has TOTP enrolled. */
export async function hydrateMfaAfterSessionRestore<
  TProfile extends MfaProfilePhone,
>(params: {
  config: MfaAppConfig;
  user: User;
  session: Session;
  profile: TProfile;
  rememberMe: boolean;
  rateLimit: MfaRateLimitState;
  setRateLimit: Dispatch<SetStateAction<MfaRateLimitState>>;
}): Promise<MfaHydrateAfterSessionRestoreResult<TProfile>> {
  const {
    config,
    user,
    session,
    profile,
    rememberMe,
  } = params;

  const base = await buildMfaTwoFactorFromSession(
    config.tokenStorageKey,
    profile.phone
  );

  if (!base.pendingVerification || !base.mfaFactorId) {
    return {
      twoFactor: base,
      pendingSession: null,
      needsMfaChallenge: false,
    };
  }

  const supabase = getSupabase(config.tokenStorageKey);
  const totpFactor = await mfaGetVerifiedTotpFactor(supabase);
  if (!totpFactor) {
    return {
      twoFactor: {
        ...base,
        pendingVerification: false,
        mfaFactorId: null,
        smsError: "MFA is misconfigured. Contact support.",
      },
      pendingSession: null,
      needsMfaChallenge: false,
    };
  }

  const pendingSession: MfaPendingSession<TProfile> = {
    user,
    session,
    profile,
    rememberMe,
  };

  const challenged = await mfaChallengeTotpFactor(
    supabase,
    totpFactor.id
  );

  return {
    twoFactor: {
      ...base,
      pendingVerification: true,
      mfaFactorId: totpFactor.id,
      mfaChallengeId: challenged.challengeId,
      smsError: challenged.error,
      maskedPhone: null,
    },
    pendingSession,
    needsMfaChallenge: true,
  };
}


export function createInitialTwoFactorState(): TwoFactorState {
  return {
    enabled: false,
    pendingVerification: false,
    maskedPhone: null,
    smsError: null,
    mfaFactorId: null,
    mfaChallengeId: null,
  };
}

/**
 * Generate a fresh batch of single-use recovery codes via the
 * `mfa-recovery-generate` edge function. Requires an aal2 session (the user
 * has just completed phone enrollment). Returns the plaintext codes once;
 * only hashes are persisted server-side.
 */
export async function mfaGenerateRecoveryCodes(
  config: MfaAppConfig
): Promise<{ error: string | null; recoveryCodes: string[] }> {
  const supabase = getSupabase(config.tokenStorageKey);
  try {
    // The aal2 claim is only present after the freshly-verified factor is
    // reflected in the access token. Refresh so the edge function receives an
    // aal2 token rather than the stale aal1 one from before enrollment.
    await supabase.auth.refreshSession();
    const { data, error } = await supabase.functions.invoke<{
      codes?: string[];
      error?: string;
    }>("mfa-recovery-generate", { body: {} });
    if (error) {
      return { error: "Failed to generate recovery codes.", recoveryCodes: [] };
    }
    if (data?.error || !Array.isArray(data?.codes)) {
      return {
        error: data?.error ?? "Failed to generate recovery codes.",
        recoveryCodes: [],
      };
    }
    return { error: null, recoveryCodes: data.codes };
  } catch {
    return { error: "Failed to generate recovery codes.", recoveryCodes: [] };
  }
}

/**
 * Consume a single-use recovery code via the `mfa-recovery-verify` edge
 * function. On success the server removes the user's existing phone factor so
 * the caller can re-enroll a new device. This does NOT produce an aal2 session
 * by itself — the UI must run the normal enroll flow on the new phone, which is
 * what elevates the session to aal2.
 */
export async function mfaVerifyRecoveryCode(
  config: MfaAppConfig,
  code: string
): Promise<{ error: string | null }> {
  const supabase = getSupabase(config.tokenStorageKey);
  const normalized = normalizeMfaRecoveryCode(code);
  if (!isValidMfaRecoveryCodeFormat(code)) {
    return { error: "Invalid recovery code format." };
  }
  try {
    const { data, error } = await supabase.functions.invoke<{
      success?: boolean;
      error?: string;
    }>("mfa-recovery-verify", { body: { code: normalized } });
    if (error) {
      const serverError = await extractFunctionErrorMessage(error);
      return {
        error: serverError ?? "Invalid or already-used recovery code.",
      };
    }
    if (data?.error || data?.success !== true) {
      return { error: data?.error ?? "Invalid or already-used recovery code." };
    }
    await logAuditEvent(
      supabase,
      "2fa_recovery_success",
      null,
      { app: config.app },
      "auth",
      config.auditActionSource()
    );
    return { error: null };
  } catch {
    return { error: "Invalid or already-used recovery code." };
  }
}

export type MfaAfterRecoveryVerifySuccessParams = {
  config: MfaAppConfig;
  setTwoFactor: Dispatch<SetStateAction<TwoFactorState>>;
  clearPendingSession?: () => void;
};

/**
 * After recovery verify removes the enrolled phone factor, the session stays at
 * aal1 while hydrate/build logic may still set pendingVerification. Clear that
 * gate and reflect MFA as disabled until re-enrollment completes.
 */
export async function mfaAfterRecoveryVerifySuccess(
  params: MfaAfterRecoveryVerifySuccessParams
): Promise<void> {
  const { config, setTwoFactor, clearPendingSession } = params;
  const supabase = getSupabase(config.tokenStorageKey);
  await supabase.auth.refreshSession();
  setTwoFactor({
    enabled: false,
    pendingVerification: false,
    maskedPhone: null,
    smsError: null,
    ...emptyMfaChallengeIds(),
  });
  clearPendingSession?.();
}

/** Best-effort extraction of the JSON `error` message from a Functions error. */
async function extractFunctionErrorMessage(
  error: unknown
): Promise<string | null> {
  const ctx = (error as { context?: unknown }).context;
  if (ctx && typeof (ctx as Response).json === "function") {
    try {
      const body = (await (ctx as Response).json()) as { error?: string };
      return body?.error ?? null;
    } catch {
      return null;
    }
  }
  return null;
}
